import { Link } from "wouter";
import { ArrowRight } from "lucide-react";
import BuyTicketsButton from "@/components/BuyTicketsButton";
import { useAuth } from "@/_core/hooks/useAuth";
import { useMyTicketsForEvent } from "@/hooks/useMyTicketsForEvent";
import { useEventClock } from "@/hooks/useEventClock";
import { isPastCalendarEvent } from "@/lib/calendarFilters";
import { hasTicketsToView, myTicketsTabHref, shouldShowBuyTickets, VIEW_MY_TICKETS_LABEL } from "@/lib/eventCta";
import { useHomepagePromotionPricing } from "@/hooks/useHomepagePromotionPricing";
import type { HomepagePromotionPricing } from "@/hooks/useHomepagePromotionPricing";
import type { HomepagePromoQuote } from "@/hooks/useHomepagePromoQuote";
import FeaturedEventPrice from "@/components/FeaturedEventPrice";
import type { CHIEvent } from "@/lib/eventsData";

export function EventAction({
  event,
  pricing,
}: {
  event: CHIEvent;
  pricing: {
    policy: HomepagePromotionPricing | null;
    quote: HomepagePromoQuote | null;
    quoteUsable: boolean;
    serverNow: number;
  };
}) {
  const { isAuthenticated } = useAuth();
  const tickets = useMyTicketsForEvent(event.slug, isAuthenticated);
  const localNow = useEventClock();
  const now = new Date(pricing.serverNow);
  const hasEnded = isPastCalendarEvent(event, now) || isPastCalendarEvent(event, localNow);
  const viewTickets = isAuthenticated && !hasEnded && hasTicketsToView({
    activeTicketCount: tickets.activeTickets.length,
    finalizing: tickets.status === "ok" && tickets.finalizing,
  });
  const canBuy = shouldShowBuyTickets({
    slug: event.slug,
    pricingMode: pricing.policy?.pricingMode,
    isSpecialEvent: event.isSpecialEvent === true,
    hasEnded,
    ticketsCheckoutEnabled: pricing.policy?.ticketsCheckoutEnabled === true,
  }, { hasBlockingTicket: tickets.hasBlockingTicket });
  const quoteUsable = pricing.quoteUsable && pricing.quote?.available === true;

  if (hasEnded) return null;
  if (tickets.status === "ok" && tickets.finalizing) return <span role="status">Preparing your tickets…</span>;
  if (viewTickets) return <Link href={myTicketsTabHref(event.slug)} className="featured-event__action">{VIEW_MY_TICKETS_LABEL} <ArrowRight size={17} /></Link>;
  if (canBuy && quoteUsable) return <BuyTicketsButton slug={event.slug} className="featured-event__action" />;
  return <Link href={`/events/${encodeURIComponent(event.slug)}`} className="featured-event__action">Event details <ArrowRight size={17} /></Link>;
}

export default function FeaturedEventAction({ event }: { event: CHIEvent }) {
  const pricing = useHomepagePromotionPricing(event.slug);
  return (
    <div className="promo-hero__featured-actions">
      <FeaturedEventPrice pricing={pricing} className="homepage-event-price--compact" />
      <EventAction event={event} pricing={pricing} />
      <style>{`.promo-hero__featured-actions{display:flex;align-items:center;justify-content:flex-end;gap:.85rem;flex-wrap:wrap}.promo-hero__featured-actions .featured-event__action{white-space:nowrap}@media(max-width:560px){.promo-hero__featured-actions{align-items:stretch;justify-content:flex-start;flex-direction:column}.promo-hero__featured-actions .featured-event__action{width:100%}}`}</style>
    </div>
  );
}
